import { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { InsightsOutlined } from "@mui/icons-material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { useThemeMode } from "../themeContext";
import { darkTokens, lightTokens } from "../theme";

type CycleRow = {
  cycleId: string;
  cycleName: string;
  year: number;
  month: number;
  status: string;
  sortKey: number;
  matched: number;
  humanReview: number;
  reviewQueue: number;
  unmatched: number;
  exception: number;
  posted: number;
  total: number;
  reconciliationRate: number;
};

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function shortLabel(row: CycleRow): string {
  const m = MONTH_SHORT[(row.month - 1) % 12] ?? "?";
  const yy = String(row.year).slice(-2);
  return `${m} ${yy}`;
}

type ChartDatum = {
  key: string;
  label: string;
  cycleName: string;
  total: number;
  reconciliationRate: number;
  matched: number;
  humanReview: number;
  reviewQueue: number;
  unmatched: number;
  exception: number;
  posted: number;
};

export function CycleStatusChart() {
  const muiTheme = useTheme();
  const { mode } = useThemeMode();
  const tokens = mode === "light" ? lightTokens : darkTokens;

  // Cross-cycle visualization: intentionally ignore the global cycle filter.
  const rows = useStableQuery(
    api.dashboardCharts.getCycleStatusBreakdown,
    {},
    [] as CycleRow[],
  ) as CycleRow[] | undefined;

  const data = useMemo<ChartDatum[]>(() => {
    if (!rows) return [];
    // v3.0.43: matchStatus="exception" was retired in favour of
    // "unmatched". Roll any historical exception count into unmatched
    // so the chart stops rendering a phantom red series. The
    // standalone `exception` field is kept (set to 0) so downstream
    // code that reads it doesn't NaN-out.
    return rows.map((r) => ({
      key: r.cycleId,
      label: shortLabel(r),
      cycleName: r.cycleName,
      total: r.total,
      reconciliationRate: r.reconciliationRate,
      matched: r.matched,
      humanReview: r.humanReview,
      reviewQueue: r.reviewQueue ?? 0,
      unmatched: r.unmatched + (r.exception ?? 0),
      exception: 0,
      posted: r.posted,
    }));
  }, [rows]);

  const totals = useMemo(() => {
    let total = 0;
    let matched = 0;
    for (const r of data) {
      total += r.total;
      matched += r.matched;
    }
    const rate = total > 0 ? Math.round((matched / total) * 1000) / 10 : 0;
    return { total, matched, rate };
  }, [data]);

  // Semantic chart colors — resolved from the active theme tokens so the
  // chart palette automatically flips with light/dark mode.
  const colors = useMemo(() => {
    const errorRed = mode === "light" ? "#d63031" : "#ff7675";
    const postedPurple = mode === "light" ? "#7c3aed" : "#a78bfa";
    return {
      matched: tokens.metricSuccess,
      humanReview: tokens.metricInfo,
      unmatched: tokens.metricWarning,
      exception: errorRed,
      posted: postedPurple,
      axis: tokens.textSecondary,
      grid: mode === "light" ? "rgba(15,23,42,0.07)" : "rgba(255,255,255,0.06)",
      tooltipBg: tokens.bgElevated,
      tooltipBorder: tokens.border,
      tooltipText: tokens.textPrimary,
    };
  }, [mode, tokens]);

  // We render NON-STACKED filled-line areas: each series sits on its own
  // baseline at y=0 so the user sees the true magnitude of each status per
  // cycle. A previous stacked version was misleading because the topmost
  // series ("exception") visually appeared dominant even when its actual
  // count was zero (the stack was just adding it on top of the others).
  // Independent areas with semi-transparent fills + thicker strokes solve
  // this: a status with 0 transactions is invisible, a status with N
  // transactions sits at exactly y=N regardless of the others.

  // Card chrome ------------------------------------------------------------

  const cardSx = {
    background: `linear-gradient(135deg, ${alpha("#6c5ce7", 0.04)}, ${alpha(
      "#00cec9",
      0.03,
    )})`,
    border: `1px solid ${muiTheme.palette.divider}`,
    position: "relative" as const,
    overflow: "hidden" as const,
  };

  if (rows === undefined) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <InsightsOutlined sx={{ color: "var(--c-link-accent)" }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Reconciliation Trend Across Cycles
            </Typography>
          </Stack>
          <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading cycle breakdown…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3, textAlign: "center" }}>
          <InsightsOutlined
            sx={{ fontSize: 40, color: "var(--c-link-accent)", opacity: 0.7, mb: 1 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            No cycle data yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reconciliation cycles will appear here once transactions are imported.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Custom tooltip ---------------------------------------------------------

  const CustomTooltip = (props: any) => {
    if (!props.active || !props.payload || props.payload.length === 0) return null;
    const datum: ChartDatum | undefined = props.payload[0]?.payload;
    if (!datum) return null;
    const rows: Array<{ label: string; value: number; color: string }> = [
      { label: "Matched", value: datum.matched, color: colors.matched },
      { label: "Review Queue", value: datum.reviewQueue, color: colors.humanReview },
      { label: "Unmatched", value: datum.unmatched, color: colors.unmatched },
      { label: "Posted to QB", value: datum.posted, color: colors.posted },
    ];
    return (
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: 2,
          background: colors.tooltipBg,
          border: `1px solid ${colors.tooltipBorder}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          minWidth: 200,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: colors.tooltipText, mb: 0.25 }}
        >
          {datum.cycleName}
        </Typography>
        <Typography variant="caption" sx={{ color: "var(--c-text-secondary)" }}>
          {datum.total} transactions · {datum.reconciliationRate}% reconciled
        </Typography>
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: `1px dashed ${colors.tooltipBorder}`,
            display: "grid",
            rowGap: 0.5,
          }}
        >
          {rows.map((r) => (
            <Stack
              key={r.label}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: r.color,
                    boxShadow: `0 0 0 2px ${alpha(r.color, 0.15)}`,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: colors.tooltipText, fontWeight: 500 }}
                >
                  {r.label}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: colors.tooltipText, fontWeight: 700 }}
              >
                {r.value}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    );
  };

  // Chart ------------------------------------------------------------------

  const legendStyle: React.CSSProperties = {
    fontSize: 12,
    color: colors.axis,
    cursor: "pointer",
  };

  const axisProps = {
    tick: { fill: colors.axis, fontSize: 12 },
    tickLine: { stroke: colors.grid },
    axisLine: { stroke: colors.grid },
  };

  const chart = (
    <AreaChart data={data} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
      <defs>
        {(
          [
            ["gMatched", colors.matched],
            ["gReview", colors.humanReview],
            ["gUnmatched", colors.unmatched],
            ["gPosted", colors.posted],
          ] as const
        ).map(([id, c]) => (
          <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity={0.45} />
            <stop offset="100%" stopColor={c} stopOpacity={0.02} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="4 4" stroke={colors.grid} vertical={false} />
      <XAxis dataKey="label" {...axisProps} />
      <YAxis allowDecimals={false} domain={[0, (max: number) => Math.ceil(max * 1.1) || 10]} {...axisProps} />
      <Tooltip content={<CustomTooltip />} cursor={{ stroke: alpha("#6c5ce7", 0.4), strokeWidth: 1, strokeDasharray: "3 3" }} />
      <Legend
        verticalAlign="top"
        align="right"
        height={28}
        iconType="circle"
        wrapperStyle={legendStyle}
      />
      {/* Non-stacked areas: every series sits on its OWN baseline at y=0, so
          the absolute count per cycle is read directly off the Y axis.
          A series with 0 transactions in a cycle is invisible there. */}
      <Area
        type="monotone"
        dataKey="matched"
        name="Matched"
        stroke={colors.matched}
        strokeWidth={2.5}
        fill="url(#gMatched)"
        dot={{ r: 3, fill: colors.matched, strokeWidth: 0 }}
        activeDot={{ r: 5, fill: colors.matched, stroke: colors.tooltipBg, strokeWidth: 2 }}
        isAnimationActive
      />
      <Area
        type="monotone"
        dataKey="reviewQueue"
        name="Review Queue"
        stroke={colors.humanReview}
        strokeWidth={2.5}
        fill="url(#gReview)"
        dot={{ r: 3, fill: colors.humanReview, strokeWidth: 0 }}
        activeDot={{ r: 5, fill: colors.humanReview, stroke: colors.tooltipBg, strokeWidth: 2 }}
        isAnimationActive
      />
      <Area
        type="monotone"
        dataKey="unmatched"
        name="Unmatched"
        stroke={colors.unmatched}
        strokeWidth={2.5}
        fill="url(#gUnmatched)"
        dot={{ r: 3, fill: colors.unmatched, strokeWidth: 0 }}
        activeDot={{ r: 5, fill: colors.unmatched, stroke: colors.tooltipBg, strokeWidth: 2 }}
        isAnimationActive
      />
      {/* v3.0.43: matchStatus="exception" was retired (merged into "unmatched")
          on 2026-05-14. v3.1.3 (2026-05-22): "In Review" (humanReview, barely
          used) replaced by "Review Queue" (reviewQueue = postingStatus="pending_review"
          count) so Michelle sees her actionable backlog directly on the chart. */}
      <Area
        type="monotone"
        dataKey="posted"
        name="Posted to QB"
        stroke={colors.posted}
        strokeWidth={2.5}
        strokeDasharray="8 4"
        fill="url(#gPosted)"
        dot={{ r: 4, fill: colors.tooltipBg, stroke: colors.posted, strokeWidth: 2 }}
        activeDot={{ r: 5, fill: colors.posted, stroke: colors.tooltipBg, strokeWidth: 2 }}
        isAnimationActive
      />
    </AreaChart>
  );

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: alpha("#6c5ce7", 0.1),
                border: `1px solid ${alpha("#6c5ce7", 0.2)}`,
              }}
            >
              <InsightsOutlined sx={{ color: "var(--c-link-accent)" }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Reconciliation Trend Across Cycles
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Status breakdown per monthly cycle · {data.length}{" "}
                {data.length === 1 ? "cycle" : "cycles"}
              </Typography>
            </Box>
          </Stack>

          {/* Totals chips removed — redundant with the hero card above */}
        </Stack>

        <Box sx={{ width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            {chart}
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
